import { ITopicHeaders, MessageBroker, PostLoginActionNameEnum } from '@sparkware/uc-sdk-core';
import { PostLoginChannel } from '@sparkware/uc-sdk-core/lib/message-broker/channels/post-login/post-login.channel';
import { SessionChannel } from '@sparkware/uc-sdk-core/lib/message-broker/channels/session-manager/session.channel';
import Container, { Service } from 'typedi';
import PostLoginApi from '../../../APIs/PostLoginApi';
import DeferredObject from '../../../Modules/Utils/DeferredObject';
import { UserContextToken, UserDataStoreDeferredObjectToken } from '../../injection-tokens';
import { ILogger, LoggerProvider } from '../logger';
import { NativeUtils } from '../native/native.utils';
import { IUserContext } from '../user-context/user-context-interface';
import { Utils } from '../utils';
import { UrlUtils } from '../utils/urlUtils';
import { PostLoginActionFactory } from './actions/post-login-action.factory';
import { PostLoginExcludedPagesArray, PostLoginFlowExecutionType } from './enums';
import { IPostLoginActionsConfigurationModel } from './models/IPostLoginActionsConfigurationModel';
import { IPostLoginActionOptions } from './models/IPostLoginActionsState';
import { IPostLoginResponseModel } from './models/IPostLoginResponseModel';
import { PostLoginEventEmitter } from './post-login.eventEmitter';
import { PostLoginStorage } from './post-login.storage';
import { ISessionUserData } from '../session-manager/interfaces/ISessionUserData';

const MB_HEADER: ITopicHeaders = { publisher: 'PostLoginService' };

@Service()
export class PostLoginService {
    private readonly _actionFactory: PostLoginActionFactory;
    private readonly _logger: ILogger;
    private readonly _postLoginChannel: PostLoginChannel;
    private readonly _postLoginEventEmitter: PostLoginEventEmitter;
    private readonly _sessionChannel: SessionChannel;
    private readonly _storage: PostLoginStorage;
    private readonly _urlUtils: UrlUtils;
    private readonly _userContext: IUserContext;
    private readonly _userDataStoreDeferredObject: DeferredObject<ISessionUserData>;
    private readonly _utils: Utils;

    private _isRunning: boolean;

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('PostLoginService');
        this._storage = Container.get(PostLoginStorage);
        this._urlUtils = Container.get(UrlUtils);
        this._utils = Container.get(Utils);
        this._userContext = Container.get(UserContextToken);
        this._postLoginChannel = MessageBroker.getInstance().postLogin;
        this._sessionChannel = MessageBroker.getInstance().session;
        this._userDataStoreDeferredObject = Container.get(UserDataStoreDeferredObjectToken);
        this._actionFactory = Container.get(PostLoginActionFactory);
        this._postLoginEventEmitter = Container.get(PostLoginEventEmitter);
        this._isRunning = false;
    }

    public getActions = async (): Promise<Array<IPostLoginActionsConfigurationModel>> => {
        const { response, errorResponse } = await PostLoginApi.GetActions();
        if (response) {
            return response as Array<IPostLoginActionsConfigurationModel>;
        }

        this._logger.error(`getActions: ${JSON.stringify(errorResponse)}`);

        return [];
    };

    public storeActions = (actions: Array<IPostLoginActionsConfigurationModel>): void => {
        this._storage.storeActions(actions || []);
    };

    public storeActionOptions = (options?: IPostLoginActionOptions): void => {
        this._storage.storeActionsOptions(options);
    };

    public resetState = (): void => {
        this._logger.debug(`resetState: flow state will be reseted`);
        this._storage.updateState(null);
        this._isRunning = false;
    };

    public getActionOptions = (): IPostLoginActionOptions => {
        const state = this._storage.getState();
        return state.actionsOptions;
    };

    public getIsCompleted = (): boolean => {
        const state = this._storage.getState();
        return state.completed;
    };

    public completeAction(actionName: PostLoginActionNameEnum) {
        this._logger.debug(`completeAction: actionName=${actionName}: start.`);
        this._postLoginChannel.topics.completeAction.publish(MB_HEADER, { actionName });
    }

    public executeActions = async (): Promise<PostLoginFlowExecutionType> => {
        this._logger.debug(`executeActions: start.`);
        this._setCorrelationIDIfUnset();
        const state = this._storage.getState();
        const correlationID = state.correlationID;

        const canExecute = await this._canExecuteActions();
        if (!canExecute) {
            this._logger.log(`executeActions: Post login actions will be skipped.`);

            this._postLoginEventEmitter.sendFlowSkipped(correlationID);
            return PostLoginFlowExecutionType.Skipped;
        }

        this._isRunning = true;

        const actions = await this._storage.getActions();
        let actionsToExecute: Array<IPostLoginActionsConfigurationModel> = actions;
        const wasInterrupted: boolean = !state.completed && !!state.lastActionInExecution;
        if (wasInterrupted) {
            this._setInterruptedState();

            const indexOfAction = actions.findIndex(
                (action) => action.Id === state.lastActionInExecution.Id,
            );
            if (indexOfAction >= 0) {
                actionsToExecute = actions.slice(indexOfAction);
            }

            this._postLoginEventEmitter.sendFlowResumed(
                state.lastActionInExecution.Name,
                actionsToExecute.map((action) => action.Name).join(', '),
                correlationID,
            );
        } else {
            this._postLoginEventEmitter.sendFlowStart(
                actionsToExecute.map((action) => action.Name).join(', '),
                correlationID,
            );
        }

        this._logger.debug(
            `executeActions: actions length: ${actionsToExecute.length}, actions:`,
            actionsToExecute,
        );

        if (actionsToExecute.length) {
            for (const action of actionsToExecute) {
                try {
                    this._setActionInExecution(action);
                    await this._executeAction(action);
                } catch (err) {
                    this._logger.error(`executeActions: actionName: ${action.Name}`, err);
                    this._postLoginEventEmitter.sendFlowError(err, correlationID);
                }
            }
        }

        this._completeActions();

        this._logger.debug(`executeActions: end.`);

        this._postLoginEventEmitter.sendFlowCompleted(correlationID);

        NativeUtils.showBottomBar(true);

        return wasInterrupted
            ? PostLoginFlowExecutionType.Interrupted
            : PostLoginFlowExecutionType.Regular;
    };

    private _executeAction = async (action: IPostLoginActionsConfigurationModel) => {
        this._logger.debug(`_executeAction: actionName=${action?.Name}: trying to execute`);

        const actionToExecute = this._actionFactory.create(action?.Name, this._storage.getState());
        await actionToExecute.execute();
    };

    private _canExecuteActions = async (): Promise<boolean> => {
        const state = this._storage.getState();
        this._logger.debug(`_canExecuteActions: start.`);
        const isClientMode = this._urlUtils.isClientMode();
        const isBossMode = this._utils.isBossMode();
        await this._userDataStoreDeferredObject.promise;
        const isAuthenticated = this._userContext.IsAuthenticated;

        let canExecute: boolean =
            !this._isRunning &&
            !state.completed &&
            isAuthenticated &&
            !isClientMode &&
            !isBossMode &&
            !this._isExcludedPage();
        if (canExecute) {
            const isExecutionRequired = await this._isExecutionRequired();
            canExecute = canExecute && (isExecutionRequired || !!state.lastActionInExecution);
        }

        this._logger.debug(`_canExecuteActions: result: ${canExecute}`);

        return canExecute;
    };

    private _isExecutionRequired = async (): Promise<boolean> => {
        const { response, errorResponse } = await PostLoginApi.IsExecutionRequired();
        if (response) {
            const formattedResponse: IPostLoginResponseModel = response;
            this._logger.debug(`_isExecutionRequired, response:`, formattedResponse);

            return formattedResponse?.PostLoginRequired || false;
        }

        this._logger.error(`_isExecutionRequired, error:`, errorResponse);

        return false;
    };

    private _isExcludedPage = (): boolean => {
        const currentLocation = this._urlUtils.getCurrentUrl().toLowerCase();

        const isPageExcluded = PostLoginExcludedPagesArray.some(
            (excludedPath) => currentLocation.indexOf(excludedPath) > 0,
        );

        this._logger.debug(`_isExcludedPage: result ${isPageExcluded}`);

        return isPageExcluded;
    };

    private _setActionInExecution = (action: IPostLoginActionsConfigurationModel): void => {
        this._logger.debug(`_setActionInExecution: called with action:`, action);
        const state = this._storage.getState();

        state.lastActionInExecution = action;

        this._storage.updateState(state);
    };

    private _setInterruptedState = (): void => {
        this._logger.debug(
            `_setInterruptedState: PostLogin flow was interrupted, updating flow state...`,
        );
        const state = this._storage.getState();
        state.interrupted = true;

        this._storage.updateState(state);
    };

    private _completeActions = (): void => {
        this._logger.debug(`_completeActions: marking post login actions flow as completed...`);

        const state = this._storage.getState();

        state.completed = true;
        this._isRunning = false;

        this._storage.updateState(state);

        this._notifyPostLoginActionsCompleted();
    };

    private _setCorrelationIDIfUnset = (): void => {
        const state = this._storage.getState();
        if (state.correlationID) {
            this._logger.debug(
                `setCorrelationID: flow already have a correlationID value set to: ${state.correlationID}`,
            );
        }

        const correlationID = this._utils.getCorrelationId();
        this._logger.debug(
            `setCorrelationID: flow correlation id will be set to: ${correlationID}`,
        );
        state.correlationID = correlationID;
        this._storage.updateState(state);
    };

    private _notifyPostLoginActionsCompleted = () => {
        this._sessionChannel.topics.postLoginActionsFinished.publish(MB_HEADER, {});
    };
}
